import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RED} from "@/shared/constants.js";
import { STEP_PATHS } from "@/modules/Quote/constants.js";
import { formatDate } from "@/shared/utils/dateHelpers.js";
import { Field } from "@/shared/components/Field.jsx";
import { TextInput } from "@/shared/components/TextInput.jsx";
import { RadioCard } from "@/shared/components/RadioCard.jsx";
import { Toggle } from "@/shared/components/Toggle.jsx";
import { SectionTitle } from "@/shared/components/SectionTitle.jsx";
import { QuoteStrip } from "@/modules/Quote/components/QuoteStrip.jsx";
import { Spacer } from "@/shared/components/Spacer.jsx";
import Alert from "@/shared/components/Alert.jsx";
import Loader from "@/shared/components/Loader.jsx";
import { btnPrimary, dis } from "@/shared/utils/styles.js";

import {
  buildCoverageGroupSavePayload,
  findSubmissionGroup,
  hydrateSubmissionAnswers,
  isQuestionVisible as isVisibilityQuestionVisible,
  mergeAnswerMaps,
  recomputeHiddenQuestionIds,
  requiredQuestionsAnswered,
  saveSubmissionQuestionAnswers,
  sortedOptions,
  sortedQuestions,
} from "@/modules/Quote/api/questionsApi.js";
import { refreshPaymentOrder } from "@/modules/Payment/services/paymentOrderService.js";

import { useStore } from "@/shared/store/useStore.js";
import questionsStore from "@/modules/Quote/store/questionsStore.js";
import coverageStore from "@/modules/Quote/store/coverageStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";
import { useQuoteSnapshot } from "@/modules/Quote/utils/useQuoteSnapshot.js";

export default function CoveragePage() {
  const navigate = useNavigate();

  // ── Store reads ─────────────────────────────────────────────────────────
  const submissionQuestionGroups   = useStore(questionsStore, (s) => s.submissionQuestionGroups);
  const submissionQuestionsLoading = useStore(questionsStore, (s) => s.submissionQuestionsLoading);
  const submissionQuestionsError   = useStore(questionsStore, (s) => s.submissionQuestionsError);
  const coverageAnswers            = useStore(questionsStore, (s) => s.coverageAnswers);
  const impactAnswers              = useStore(questionsStore, (s) => s.impactAnswers);
  const hiddenQuestionIds          = useStore(questionsStore, (s) => s.hiddenQuestionIds);
  const flowSubmissionId           = useStore(submissionStore, (s) => s.flowSubmissionId);
  const updateRatingError          = useStore(coverageStore, (s) => s.updateRatingError);
  const updateRatingLoading        = useStore(coverageStore, (s) => s.updateRatingLoading);

  const {
    quoteStripAnnualTotal, snapshotLimits, snapshotPolicyLine, snapshotState,
    quoteStripAmountPending,
  } = useQuoteSnapshot();

  // ── Group + helpers ─────────────────────────────────────────────────────
  const coverageGroup = findSubmissionGroup(submissionQuestionGroups, /coverage\s*options/i);
  const heading = coverageGroup?.groupName || "Coverage options";

  // Recompute the submission-wide hidden set from the whole tree + all live
  // answer maps. Reads directly off the store (not the render-time snapshot)
  // so it's correct even when called synchronously right after an answer write.
  const recomputeHidden = () => {
    const liveAnswers = mergeAnswerMaps(
      questionsStore.questionAnswers,
      questionsStore.coverageAnswers,
      questionsStore.underwritingAnswers,
    );
    questionsStore.setHiddenQuestionIds(
      recomputeHiddenQuestionIds(
        questionsStore.submissionQuestionGroups,
        liveAnswers,
        questionsStore.showTargetQuestionIds,
      ),
    );
  };

  // Hydrate saved answers + defaults from the submission tree. Runs once
  // per group instance — never clobbers in-progress edits because we merge
  // saved/default values UNDER the current store values.
  useEffect(() => {
    if (!coverageGroup) return;
    const { answers, impacts } = hydrateSubmissionAnswers(coverageGroup);
    if (Object.keys(answers).length > 0) {
      questionsStore.coverageAnswers = { ...answers, ...questionsStore.coverageAnswers };
    }
    if (Object.keys(impacts).length > 0) {
      questionsStore.impactAnswers = { ...impacts, ...questionsStore.impactAnswers };
    }
    // Prefill may have brought in cross-group answers configured after this
    // submission's last save — recompute so their Hide/Show rules take effect.
    recomputeHidden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverageGroup]);

  const priorActsQ = (coverageGroup?.questions || []).find(
    (q) => /prior\s*acts/i.test(String(q?.questionText || "")),
  );
  const priorActsSelOpt = priorActsQ
    ? (priorActsQ.options || []).find(
      (o) => String(o.id) === String(coverageAnswers[String(priorActsQ.id)]),
    )
    : null;
  const priorActsIsNo = priorActsSelOpt
    && /^no/i.test(String(priorActsSelOpt.optionValue || priorActsSelOpt.optionLabel || ""));

  // A question is on-screen unless it's the waiver row, which is hidden until
  // prior-acts is answered "No". This bespoke rule is combined (logical AND)
  // with the server-driven Visibility rule — both must pass. Hidden questions
  // never gate.
  const isWaiverVisible = (q) =>
    !(/waiver/i.test(String(q?.questionText || "")) && !priorActsIsNo);
  const isVisible = (q) => isWaiverVisible(q) && isVisibilityQuestionVisible(q, hiddenQuestionIds);

  // canAdvance: every required (visible) question — and its required
  // follow-up impacts — must be answered before Continue.
  const canAdvance = () =>
    requiredQuestionsAnswered(coverageGroup?.questions, coverageAnswers, {
      isVisible,
      impactAnswers,
      checkImpacts: true,
    });

  const isOptionSelected = (q, optionId) => {
    const ans = coverageAnswers[String(q.id)];
    if (Array.isArray(ans)) return ans.includes(String(optionId));
    return String(ans) === String(optionId);
  };

  // ── Answer setters ──────────────────────────────────────────────────────
  const setRadio = (q, optionId) => {
    const next = { ...coverageAnswers, [String(q.id)]: String(optionId) };
    if (/prior\s*acts/i.test(String(q?.questionText || ""))) {
      const opt = (q.options || []).find((o) => String(o.id) === String(optionId));
      const isYes = opt && /^yes/i.test(String(opt.optionValue || opt.optionLabel || ""));
      if (isYes) {
        const waiverQ = (coverageGroup?.questions || []).find(
          (wq) => /waiver/i.test(String(wq?.questionText || "")),
        );
        if (waiverQ) {
          const defaultOpt = (waiverQ.options || []).find((o) => o?.isDefault);
          const wqid = String(waiverQ.id);
          if (defaultOpt) next[wqid] = String(defaultOpt.id);
          else delete next[wqid];
        }
      }
    }
    questionsStore.coverageAnswers = next;
    recomputeHidden();
  };

  const toggleCheckbox = (q, optionId) => {
    const qid = String(q.id);
    const cur = Array.isArray(coverageAnswers[qid]) ? coverageAnswers[qid] : [];
    const oid = String(optionId);
    const next = cur.includes(oid) ? cur.filter((x) => x !== oid) : [...cur, oid];
    questionsStore.coverageAnswers = { ...coverageAnswers, [qid]: next };
    recomputeHidden();
  };

  const setText = (q, value) => {
    questionsStore.coverageAnswers = { ...coverageAnswers, [String(q.id)]: value };
  };

  const setImpact = (impactId, value) => {
    questionsStore.impactAnswers = { ...impactAnswers, [String(impactId)]: value };
  };

  // ── Continue: save coverage answers, refresh order, advance ────────────
  const handleContinue = async () => {
    if (updateRatingLoading) return;
    if (!flowSubmissionId) { submissionStore.step = 5; navigate(STEP_PATHS[5]); return; }
    const payload = buildCoverageGroupSavePayload(
      flowSubmissionId, submissionQuestionGroups, coverageAnswers, impactAnswers, hiddenQuestionIds,
    );
    if (!payload) { submissionStore.step = 5; navigate(STEP_PATHS[5]); return; }

    coverageStore.updateRatingError = null;
    coverageStore.updateRatingLoading = true;
    try {
      await saveSubmissionQuestionAnswers(payload);
      await refreshPaymentOrder(flowSubmissionId);
      submissionStore.step = 5;
      navigate(STEP_PATHS[5]);
    } catch (err) {
      coverageStore.updateRatingError = err?.message || "Could not save coverage answers.";
    } finally {
      coverageStore.updateRatingLoading = false;
    }
  };

  const renderImpacts = (option) => {
    const impacts = Array.isArray(option?.impacts) ? option.impacts : [];
    if (impacts.length === 0) return null;
    return (
      <div style={{ marginBottom: 8, paddingLeft: 6 }}>
        {impacts.map((imp) => {
          const iid = String(imp?.id);
          const val = impactAnswers[iid] ?? "";
          const inputType = String(imp?.impactInputType || "TEXTBOX").toUpperCase();
          const isDate = inputType === "DATE_PICKER" || inputType === "DATE";
          const label = imp?.impactOutput
            || (imp?.impactType ? String(imp.impactType).replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : "Details");
          const required = imp?.isRequired ? <span style={{ color: RED, marginLeft: 4 }}>*</span> : null;
          const onChange = isDate
            ? (v) => setImpact(iid, formatDate(v))
            : (v) => setImpact(iid, v);
          const placeholder = isDate ? "MM/DD/YYYY" : "";
          return (
            <Field key={iid} label={<>{label}{required}</>}>
              <TextInput
                value={val}
                onChange={onChange}
                placeholder={placeholder}
                inputMode={isDate ? "numeric" : undefined}
                maxLength={isDate ? 14 : undefined}
              />
            </Field>
          );
        })}
      </div>
    );
  };

  const renderQuestion = (q) => {
    if (!isVisible(q)) return null;

    const qid = String(q.id);
    const opts = sortedOptions(q.options);
    const required = q.isRequired ? <span style={{ color: RED, marginLeft: 4 }}>*</span> : null;

    return (
      <div key={qid}>
        <SectionTitle>{q.questionText}{required}</SectionTitle>

        {(q.questionType === "RADIO_BUTTON" || q.questionType === "YES_NO") && opts.map((o) => (
          <div key={o.id}>
            <RadioCard
              selected={isOptionSelected(q, o.id)}
              onClick={() => setRadio(q, o.id)}
              title={o.optionLabel}
              subtitle={o.optionDescription || o.description || null}
            />
            {isOptionSelected(q, o.id) && renderImpacts(o)}
          </div>
        ))}

        {q.questionType === "CHECKBOX" && opts.map((o) => (
          <div key={o.id}>
            <Toggle
              value={isOptionSelected(q, o.id)}
              onChange={() => toggleCheckbox(q, o.id)}
              label={o.optionLabel}
              description={o.optionDescription || o.description || null}
            />
            {isOptionSelected(q, o.id) && renderImpacts(o)}
          </div>
        ))}

        {q.questionType === "TEXTBOX" && (
          <TextInput
            value={coverageAnswers[qid] || ""}
            onChange={(v) => setText(q, v)}
            placeholder={opts[0]?.optionLabel || ""}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <QuoteStrip total={quoteStripAnnualTotal} limits={snapshotLimits} claims={snapshotPolicyLine} amountPending={quoteStripAmountPending} amountLabel="Your total" />
      <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 19, fontWeight: 600, color: "#1a1a1a", margin: "6px 0 4px" }}>{heading}</h2>
      <div style={{ background: "#f7f7f5", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: "#555", lineHeight: 1.6 }}>
          <strong style={{ color: "#333" }}>Limits: {snapshotLimits}</strong><br />
          <span style={{ fontSize: 12, color: "#999" }}>Auto-set by your practice state ({snapshotState}). State-mandated.</span>
        </div>
      </div>

      {submissionQuestionsLoading && <Loader label="Loading questions…" />}
      {submissionQuestionsError && (
        <Alert type="error">Couldn't load questions. {submissionQuestionsError.message}</Alert>
      )}

      {coverageGroup && Array.isArray(coverageGroup.questions) && sortedQuestions(coverageGroup.questions).map(renderQuestion)}

      <Spacer />
      <Alert type="error" message={updateRatingError} className="alert-center" />

      <button style={dis(btnPrimary, canAdvance() && !updateRatingLoading)} onClick={handleContinue}>
        {updateRatingLoading ? "Saving…" : "Continue"}
      </button>
    </>
  );
}
